import React from 'react';
import { Card, Title, Text, Grid, Group, Badge, useMantineTheme } from '@mantine/core';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ClientStatistics } from '../../../types/statistics-types';

interface ClientStatisticsChartProps {
  data: ClientStatistics[];
}

export function ClientStatisticsChart({ data }: ClientStatisticsChartProps) {
  const theme = useMantineTheme();
  
  // Ordina i dati per numero di richieste
  const sortedData = [...data].sort((a, b) => b.totalRequests - a.totalRequests);
  
  // Formatta i dati per il grafico a barre
  const barChartData = sortedData.map(client => ({
    name: client.name,
    'Richieste Totali': client.totalRequests,
    'Richieste Completate': client.completedRequests,
    'Richieste Pendenti': client.pendingRequests,
  }));
  
  // Formatta i dati per il grafico a torta
  const pieChartData = sortedData.map(client => ({
    name: client.name,
    value: client.totalRequests,
  }));
  
  // Colori per il grafico a torta
  const COLORS = [
    theme.colors.blue[5],
    theme.colors.green[5],
    theme.colors.yellow[5],
    theme.colors.orange[5],
    theme.colors.red[5],
    theme.colors.violet[5],
    theme.colors.indigo[5],
    theme.colors.cyan[5],
  ];
  
  return (
    <Card withBorder p="md">
      <Title order={3} mb="md">Statistiche per Cliente</Title>
      <Text size="sm" color="dimmed" mb="xl">
        Visualizzazione delle richieste per cliente e del loro stato
      </Text>
      
      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={barChartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray[3]} />
              <XAxis 
                dataKey="name" 
                tick={{ fill: theme.colors.gray[7] }} 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fill: theme.colors.gray[7] }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: theme.colors.gray[0],
                  borderColor: theme.colors.gray[4],
                  borderRadius: theme.radius.sm
                }}
              />
              <Legend wrapperStyle={{ paddingTop: 15 }} />
              <Bar dataKey="Richieste Totali" fill={theme.colors.blue[5]} barSize={30} />
              <Bar dataKey="Richieste Completate" fill={theme.colors.green[5]} barSize={30} />
              <Bar dataKey="Richieste Pendenti" fill={theme.colors.yellow[5]} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, md: 4 }}>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: theme.colors.gray[0],
                  borderColor: theme.colors.gray[4],
                  borderRadius: theme.radius.sm
                }}
                formatter={(value, name, props) => [`${value} richieste`, props.payload.name]}
              />
            </PieChart>
          </ResponsiveContainer>
        </Grid.Col>
      </Grid>
      
      <Title order={4} mt="xl" mb="md">Tempi di Consegna per Cliente</Title>
      <Group justify="center" gap="xl">
        {sortedData.map(client => (
          <Card key={client.clientId} withBorder p="sm" radius="md">
            <Title order={5}>{client.name}</Title>
            <Group mt="xs" gap="xs">
              <Badge color="blue" style={{ color: 'white' }}>{client.totalRequests} richieste</Badge>
              <Badge color={client.avgDeliveryTime < 15 ? 'green' : client.avgDeliveryTime < 25 ? 'yellow' : 'red'} style={{ color: 'white' }}>
                {client.avgDeliveryTime.toFixed(1)} giorni
              </Badge>
            </Group>
          </Card>
        ))}
      </Group>
    </Card>
  );
}